/* eslint-disable */

import { useTranslation } from 'react-i18next';
import useSidebarContext from '../../../../hooks/sidebar/useSidebarContext';
import { parseDateWithoutTime } from '../../../../shared/helpers/parse-date';
import PenOutlineIcon from '../../../../assets/icons/PenOutline';
import PenFilledIcon from '../../../../assets/icons/PenFilled';
import CustomIcon from '../../../../shared/components/custom-icon/CustomIcon';
import { textifyDeclaredUnit } from '../../../../shared/helpers/textify-numerical-footprint-values-with-their-units';
import ProductMass from './components/product-mass/ProductMass';
import { Locale } from '../../../../i18n';
import { useFootprintAndProduct } from '../../../../hooks/useFootprintAndProduct';
import { CREATION_METHOD } from '../../../../shared/interfaces/IFootprint';
import DownloadCalculationDetails from './components/download-calculation-details/DownloadCalculationDetails';
import DetailRow from '../detail-row/DetailRow';

const PcfGeneralDetailsSection = (): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { openSidebar } = useSidebarContext();
  const {
    footprintDetails,
    isFootprintLoading: isLoading,
    showEditButton,
  } = useFootprintAndProduct();

  return (
    <div className='bg-white p-4 md:p-5 border-round-2xl flex flex-column w-full'>
      {/* Title */}
      <div className='flex flex-row gap-3 align-items-center pb-3'>
        <div className='gap-2 flex'>
          <div className='bg-primary p-1' />
          <h5 className='m-0 p-0 font-medium'>
            {t('footprintDetailsPage.sections.pcfGeneralDetailsSection.title')}
          </h5>
        </div>
        {footprintDetails?.creation_method === CREATION_METHOD.CALCULATION &&
          footprintDetails?.id && (
            <DownloadCalculationDetails
              footprintId={footprintDetails.id}
              pdfPath={footprintDetails.s3PathPdf}
            />
          )}
        {showEditButton && (
          <CustomIcon
            icon={PenOutlineIcon}
            hoverIcon={PenFilledIcon}
            showTextOnHover
            hoverText={t('global.edit')}
            hoverTextColor='primary'
            className='cursor-pointer flex justify-content-center align-items-center'
            onClick={() => {
              openSidebar('edit-pcf-general-details-sidebar', {
                position: 'right',
                footprintDetails: footprintDetails,
              });
            }}
          />
        )}
      </div>

      {/* Pcf Id */}
      <DetailRow
        label={t('footprintDetailsPage.sections.pcfGeneralDetailsSection.pcfId')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.footprint_id ?? t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Date Created */}
      <DetailRow
        label={t('footprintDetailsPage.sections.pcfGeneralDetailsSection.dateCreated')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.date_created
            ? parseDateWithoutTime(footprintDetails.date_created, language as Locale)
            : t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Declared Unit */}
      <DetailRow
        label={t('footprintDetailsPage.sections.pcfGeneralDetailsSection.declaredUnit')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.unitary_product_amount != null &&
          footprintDetails?.pcf?.declared_unit != null
            ? footprintDetails?.pcf.unitary_product_amount +
              ' ' +
              textifyDeclaredUnit(footprintDetails?.pcf.declared_unit)
            : t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Product Mass */}
      <DetailRow
        label={t('footprintDetailsPage.sections.pcfGeneralDetailsSection.productMass')}
        isLoading={isLoading}
      >
        <ProductMass
          productMass={footprintDetails?.pcf?.product_mass_per_declared_unit}
          declaredUnit={footprintDetails?.pcf?.declared_unit}
        />
      </DetailRow>

      {/* Retro or Prospective PCF Type */}
      <DetailRow
        label={t('footprintDetailsPage.sections.pcfGeneralDetailsSection.pcfType')}
        isLoading={isLoading}
        showDivider={false}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.retro_or_prospective_pcf_type ?? t('global.notSpecified')}
        </span>
      </DetailRow>
    </div>
  );
};

export default PcfGeneralDetailsSection;
